import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { SymptomChecker } from "@/components/content/SymptomChecker";
import { FixSteps } from "@/components/content/FixSteps";
import { DIYvsDoneForYou } from "@/components/content/DIYvsDoneForYou";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { LeadFormSection } from "@/components/LeadFormSection";

const NoCallsFromGoogleMaps = () => {
  const symptoms = [
    "Your Google Business Profile shows few or no calls",
    "You're visible on Google Maps but getting no inquiries",
    "Competitors nearby get more calls despite similar ratings",
    "Profile views are decent but actions are minimal",
    "Your profile appears for 'near me' searches but doesn't convert",
    "Website clicks from GBP are low or nonexistent"
  ];

  const rootCauses = [
    { cause: "Incomplete business profile", impact: "Google shows less information, reducing trust" },
    { cause: "No photos or outdated photos", impact: "Profiles without images get 42% fewer requests for directions" },
    { cause: "Few or negative reviews", impact: "Customers choose competitors with better review profiles" },
    { cause: "Wrong business category", impact: "You're not showing for the right searches" },
    { cause: "Missing services list", impact: "Google can't match you with specific service searches" },
    { cause: "No Google posts", impact: "Active profiles rank higher and appear more trustworthy" },
    { cause: "Incorrect business hours", impact: "Customers think you're closed or unreliable" },
    { cause: "Poor quality or spam-like reviews", impact: "Customers distrust obviously fake-looking reviews" }
  ];

  const fixSteps = [
    { step: "Complete every field in your Google Business Profile", details: "Add business description, services, attributes, and all contact info" },
    { step: "Upload 20+ high-quality photos", details: "Include team photos, vehicle/equipment, before/afters, and action shots" },
    { step: "Actively collect and respond to reviews", details: "Ask happy customers to review; respond professionally to all reviews" },
    { step: "Verify your primary and secondary categories", details: "Primary should be 'Lawn Care Service' or 'Landscaper' depending on focus" },
    { step: "Add all services with descriptions", details: "List every service you offer with brief descriptions and pricing hints" },
    { step: "Post weekly updates to your profile", details: "Share seasonal tips, completed projects, or special offers" },
    { step: "Ensure NAP consistency", details: "Name, address, phone must match exactly across website and all directories" }
  ];

  const faqs = [
    { question: "Why am I not getting calls from Google Maps?", answer: "The most common reasons are: incomplete profile, few reviews, no photos, or wrong business category. Google prioritizes profiles that are complete, active, and have strong reviews." },
    { question: "How many reviews do I need to get calls from GBP?", answer: "Aim for at least 20 reviews with a 4.5+ average rating. Profiles with more reviews get more clicks. However, quality and recency matter too—recent reviews signal an active business." },
    { question: "Do photos really affect Google Business Profile performance?", answer: "Significantly. Profiles with photos get 42% more requests for directions and 35% more website clicks. Upload at least 20 photos including your team, equipment, and before/after work examples." },
    { question: "What's the best category for lawn care on Google Business?", answer: "Primary category should be 'Lawn Care Service' for maintenance-focused businesses or 'Landscaper' for design/installation. Add secondary categories for other services like 'Tree Service' or 'Irrigation Company.'" },
    { question: "How often should I post to my Google Business Profile?", answer: "At least weekly. Posts expire after 7 days and active profiles rank higher. Share seasonal tips, completed projects, special offers, or community involvement. Regular posting signals an active, trustworthy business." },
    { question: "Why do competitors get more calls with fewer reviews?", answer: "They likely have better profile completion, more photos, correct categories, or are responding to reviews consistently. Review count isn't everything—profile quality and engagement matter significantly." },
    { question: "Should I respond to all Google reviews?", answer: "Yes. Responding to reviews (positive and negative) shows you're engaged and care about customer feedback. It also gives you another opportunity to include keywords and service information naturally." },
    { question: "What's NAP consistency and why does it matter?", answer: "NAP = Name, Address, Phone. These must be identical across your Google Business Profile, website, and all online directories. Inconsistencies confuse Google and can hurt your local ranking." },
    { question: "How do I get more reviews for my lawn care business?", answer: "Ask in person at the end of successful jobs. Send a follow-up text or email with a direct link to your Google review page. Make it as easy as possible—every extra step reduces review completion." },
    { question: "Can I rank in Google Maps without a physical location?", answer: "Yes. Service area businesses can hide their address and specify service areas instead. You'll rank for searches within your service area, though typically behind businesses with physical locations in that area." }
  ];

  const links = [
    { to: "/websites/", label: "Website Subscription Pricing" },
    { to: "/growth-add-ons/gbp/", label: "Google Business Profile Optimization" },
    { to: "/comparisons/website-only-vs-website-plus-gbp/", label: "Website vs Website + GBP" },
    { to: "/resources/google-business-profile-for-lawn-care/", label: "GBP Guide for Lawn Care" }
  ];

  return (
    <>
      <SEO
        title="Not Getting Calls from Google Business Profile? Fix It | Leaf Rush Marketing"
        description="Is your Google Business Profile visible but not generating calls? Learn why and get actionable fixes to turn profile views into phone calls."
        url="https://www.leafrushmarketing.com/website-fixes/no-calls-from-google-maps/"
      />
      <PageSchema
        url="https://www.leafrushmarketing.com/website-fixes/no-calls-from-google-maps/"
        title="Not Getting Calls from Google Business Profile? Fix It"
        description="Is your Google Business Profile visible but not generating calls? Learn why and get fixes."
        faqs={faqs}
      />
      <LastUpdated />

      <article className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <header className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Not Getting Calls from Google Business Profile? Here's the Fix
            </h1>
            <p className="text-xl text-muted-foreground">
              You're visible on Google Maps, but the phone isn't ringing. Let's turn profile views into customer calls.
            </p>
          </header>

          <TopCTA />

          <SymptomChecker symptoms={symptoms} title="Signs Your GBP Isn't Converting" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">Root Causes: Why Visibility Doesn't Equal Calls</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {rootCauses.map((item, index) => (
                <div key={index} className="p-4 bg-muted rounded-lg">
                  <h3 className="font-semibold text-foreground">{item.cause}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{item.impact}</p>
                </div>
              ))}
            </div>
          </section>

          <MidPageCTA />

          <FixSteps steps={fixSteps} title="7 Steps to Get More Calls from Google Maps" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">The Incomplete Profile Problem</h2>
            <div className="prose prose-lg max-w-none text-foreground">
              <p>
                Most lawn care businesses claim their Google Business Profile but never optimize it. They add basic info and wait for calls that never come. Meanwhile, competitors with complete profiles dominate the local pack.
              </p>
              <p>
                <strong>Google rewards complete, active profiles with better visibility and more customer actions.</strong> Every empty field, missing photo, and unanswered review is costing you potential customers.
              </p>
              <p>
                At <a href="/growth-add-ons/gbp/" className="text-primary hover:underline">Leaf Rush Marketing</a>, our GBP optimization service transforms basic profiles into lead-generating machines. We handle complete optimization, review strategy, and ongoing posting—so your profile works as hard as you do.
              </p>
            </div>
          </section>

          <DIYvsDoneForYou />

          <FAQSection faqs={faqs} />

          <InternalLinks links={links} title="Related Resources" />

          <BottomCTA />

          <LeadFormSection title="Get Your GBP Optimized" />
        </div>
      </article>
    </>
  );
};

export default NoCallsFromGoogleMaps;
